// Breakpoints for responsive design
export const breakpoints = {
  sm: '320px',   // Mobile
  md: '768px',   // Tablet
  lg: '1024px',  // Desktop
  xl: '1440px',  // Large desktop
  '2xl': '2560px', // 2K+ ultra-wide
}

// Container max-widths
export const containerMaxWidths = {
  sm: '100%',
  md: '100%',
  lg: '100%',
  xl: '100%',
  '2xl': '1920px', // Max width for ultra-wide screens
}

// Spacing scale
export const spacing = {
  px: '1px',
  0: '0',
  1: '0.25rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  8: '2rem',
  10: '2.5rem',
  12: '3rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
  32: '8rem',
}

// Border radius - Subtle Jira-style rounding
export const radii = {
  none: '0',
  xs: '0.125rem',
  sm: '0.1875rem',
  md: '0.25rem',
  lg: '0.375rem',
  xl: '0.5rem',
  '2xl': '0.625rem',
  '3xl': '0.75rem',
  '4xl': '1rem',
  full: '9999px',
}

// Shadows - Ultra-soft Jira-style shadows
export const shadows = {
  xs: '0 1px 1px 0 rgba(9, 30, 66, 0.08)',
  sm: '0 1px 1px rgba(9, 30, 66, 0.08)',
  md: '0 2px 4px 0 rgba(9, 30, 66, 0.08)',
  lg: '0 4px 8px -2px rgba(9, 30, 66, 0.08)',
  xl: '0 8px 16px -4px rgba(9, 30, 66, 0.1)',
  '2xl': '0 12px 24px -6px rgba(9, 30, 66, 0.12)',
  '3xl': '0 20px 32px -8px rgba(9, 30, 66, 0.14)',
  inner: 'inset 0 1px 2px 0 rgba(9, 30, 66, 0.06)',
  outline: '0 0 0 2px rgba(74, 144, 226, 0.3)',
  brand: '0 0 0 1px rgba(74, 144, 226, 0.2)',
  brandLg: '0 0 0 2px rgba(74, 144, 226, 0.3)',
  secondary: '0 0 0 1px rgba(32, 201, 151, 0.2)',
}

// Z-index scale
export const zIndices = {
  hide: -1,
  auto: 'auto',
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
}

// ============================================================================
// APPLICATION-SPECIFIC TOKENS
// Custom UI constants for consistent styling across components
// ============================================================================

// Layout dimensions - Jira-style compact
export const layout = {
  header: {
    height: '56px',
  },
  sidebar: {
    widthOpen: '240px',
    widthClosed: '64px',
  },
  footer: {
    height: '64px',
  },
}

// Navigation components - Compact Jira-style
export const navigation = {
  logo: {
    size: '32px',
    fontSize: 'md',
    fontWeight: 'bold',
  },
  navItem: {
    height: '36px',
    iconSize: '20px',
    fontSize: 'sm',
    activeIndicatorWidth: '3px',
  },
}

// Menu components
export const menu = {
  notifications: {
    minWidth: '400px',
    maxHeight: '520px',
    itemPadding: { x: 6, y: 4 },
    headerPadding: { x: 6, y: 4 },
    iconSize: '22px',
    dotSize: '10px',
    emptyIconSize: '56px',
  },
  user: {
    minWidth: '280px',
    avatarSize: '48px',
    itemPadding: { x: 5, y: 4 },
    headerPadding: { x: 5, y: 5 },
    iconSize: '18px',
  },
}

// Badge components
export const badge = {
  notification: {
    size: '22px',
    fontSize: 'xs',
    fontWeight: '800',
    border: '2px',
    offset: '-8px',
  },
  status: {
    size: '20px',
    fontSize: 'xs',
    fontWeight: '700',
  },
  count: {
    minWidth: '22px',
    height: '22px',
    padding: { x: 3, y: 1 },
    fontSize: 'xs',
    fontWeight: '700',
  },
}

// Card components
export const card = {
  padding: { x: 6, y: 6 },
  borderRadius: '2xl',
  headerPadding: { x: 6, y: 4 },
  bodyPadding: { x: 6, y: 4 },
  footerPadding: { x: 6, y: 4 },
}

// Modal/Dialog components
export const dialog = {
  sizes: {
    sm: '400px',
    md: '600px',
    lg: '800px',
    xl: '1000px',
  },
  padding: { x: 6, y: 6 },
  headerPadding: { x: 6, y: 4 },
  bodyPadding: { x: 6, y: 4 },
  footerPadding: { x: 6, y: 4 },
}

// Table components
export const table = {
  rowHeight: '56px',
  cellPadding: { x: 6, y: 4 },
  headerFontWeight: '700',
  iconSize: '18px',
  actionIconSize: '20px',
}

// Form components
export const form = {
  input: {
    height: '44px',
    borderRadius: 'lg',
    fontSize: 'md',
    padding: { x: 4, y: 3 },
  },
  label: {
    fontSize: 'sm',
    fontWeight: '600',
    marginBottom: '2',
  },
  error: {
    fontSize: 'sm',
    marginTop: '1',
  },
}

// Button sizes (extended from theme)
export const button = {
  icon: {
    sm: '32px',
    md: '40px',
    lg: '48px',
  },
  iconBoxSize: {
    sm: '16px',
    md: '20px',
    lg: '24px',
  },
}

// Animations
export const animations = {
  transition: {
    fast: '0.15s ease-in-out',
    base: '0.2s ease-in-out',
    slow: '0.3s ease-in-out',
  },
  timing: {
    smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  },
  hover: {
    translateY: '-1px',
    translateX: '4px',
    scale: '1.05',
    scaleSmall: '1.02',
  },
}

// Border styles
export const borders = {
  width: {
    thin: '1px',
    medium: '2px',
    thick: '3px',
  },
  style: 'solid',
}

// Ring styles (for focus states)
export const ring = {
  width: {
    sm: '2px',
    md: '3px',
    lg: '4px',
  },
  offset: '2px',
}

// Empty state
export const emptyState = {
  iconSize: '64px',
  padding: { x: 6, y: 12 },
  titleFontSize: 'lg',
  descriptionFontSize: 'sm',
}

// Toast/notification
export const toast = {
  width: '400px',
  padding: { x: 4, y: 4 },
  borderRadius: 'xl',
  iconSize: '20px',
}

// Semantic spacing (for clearer intent)
export const semanticSpacing = {
  section: 8,      // Between major sections
  component: 6,    // Between components
  group: 4,        // Between related items
  item: 2,         // Between small items
  tight: 1,        // Minimal spacing
}
